import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetMembersQuery,
  useRemoveMemberMutation,
  useUpdateMemberRoleMutation,
  useResendInvitationMutation,
  useGetHomeQuery,
  useSetExportAccessMutation,
  useGetMemberAvailabilityQuery,
  useSetMemberWorkingHoursMutation,
} from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { backLabel, hasWorkingHours, workingHoursSummary } from "@/util/availability";
import MemberAvailabilityStatus from "@/components/team/MemberAvailabilityStatus";
import WorkingHoursDialog from "@/components/team/WorkingHoursDialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Bot,
  CalendarDays,
  MoreVertical,
  Pencil,
  Plus,
  Users,
  Trash2,
  Shield,
  ShieldCheck,
  Crown,
  Mail,
  Clock,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import InviteMemberDialog from "./components/InviteMemberDialog";
import DisplayNameDialog from "./components/DisplayNameDialog";
import TeamDialog from "./components/TeamDialog";
import { memberFirstName, memberName } from "@/util/member";
import TeamsTab from "./components/TeamsTab";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";
import Membership, { WorkingHoursRequest } from "@/model/interface/Membership";
import React from "react";

const roleIcons = {
  [MembershipRole.OWNER]: Crown,
  [MembershipRole.ADMIN]: ShieldCheck,
  [MembershipRole.MEMBER]: Shield,
  [MembershipRole.AI_SUPERVISOR]: Bot,
};

const roleColors = {
  [MembershipRole.OWNER]: "bg-amber-500",
  [MembershipRole.ADMIN]: "bg-blue-500",
  [MembershipRole.MEMBER]: "bg-gray-500",
  [MembershipRole.AI_SUPERVISOR]: "bg-info",
};

const roleLabels = {
  [MembershipRole.OWNER]: "Owner",
  [MembershipRole.ADMIN]: "Admin",
  [MembershipRole.MEMBER]: "Member",
  [MembershipRole.AI_SUPERVISOR]: "AI supervisor",
};

const statusColors = {
  [MembershipStatus.ACTIVE]: "default",
  [MembershipStatus.PENDING]: "secondary",
  [MembershipStatus.INACTIVE]: "outline",
  [MembershipStatus.REMOVED]: "destructive",
} as const;

export default function TeamMembers() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // Team management is admin/owner-only. The role is the user's role in their ACTIVE org, so it
  // updates correctly when they switch organisations. The BE also enforces this on every mutation.
  const { data: me, isLoading: meLoading } = useGetHomeQuery();
  const activeRole = me?.activeMembership?.role;
  const canManage = activeRole === MembershipRole.OWNER || activeRole === MembershipRole.ADMIN;

  const {
    data: members,
    isLoading,
    error,
  } = useGetMembersQuery(
    { status: statusFilter === "all" ? undefined : statusFilter },
    { skip: !canManage },
  );
  const [removeMember] = useRemoveMemberMutation();
  const [updateMemberRole] = useUpdateMemberRoleMutation();
  const [resendInvitation] = useResendInvitationMutation();
  const [setExportAccess] = useSetExportAccessMutation();
  const { data: availability = [] } = useGetMemberAvailabilityQuery(undefined, { skip: !canManage });
  const [setMemberWorkingHours] = useSetMemberWorkingHoursMutation();
  const isOwner = activeRole === MembershipRole.OWNER;

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<Membership | null>(null);
  const [hoursMember, setHoursMember] = useState<Membership | null>(null);
  const [nameMember, setNameMember] = useState<Membership | null>(null);
  const [isNewTeamOpen, setIsNewTeamOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "teams" ? "teams" : "members";

  const setTab = (tab: string) => {
    setSearchParams({ tab }, { replace: true });
  };

  const handleRemove = async () => {
    if (!memberToRemove) return;

    try {
      await removeMember(memberToRemove.id).unwrap();
      toast.success("Member removed successfully");
      setMemberToRemove(null);
    } catch (error: any) {
      toast.error(error.detail || "Failed to remove member");
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: MembershipRole) => {
    try {
      await updateMemberRole({
        id: memberId,
        data: { role: newRole },
      }).unwrap();
      toast.success("Member role updated successfully");
    } catch (error: any) {
      toast.error(error.detail || "Failed to update member role");
    }
  };

  const handleExportAccess = async (member: Membership, allowed: boolean) => {
    try {
      await setExportAccess({ id: member.id, data: { exportAccess: allowed ? "ALLOWED" : "NONE" } }).unwrap();
      toast.success(allowed ? "They can now export" : "Export permission removed");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Could not change the export permission"));
    }
  };

  const handleResendInvitation = async (memberId: string) => {
    try {
      await resendInvitation(memberId).unwrap();
      toast.success("Invitation resent successfully");
    } catch (error: any) {
      toast.error(error.detail || "Failed to resend invitation");
    }
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.[0] || "";
    const last = lastName?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const memberList = members ?? [];
  const availabilityById = new Map(availability.map((entry) => [String(entry.id), entry]));

  const availabilityDetail = (entry: Membership) => {
    const changesAt = entry.presence?.availabilityChangesAt;
    if (entry.availability === "UNAVAILABLE") {
      return changesAt ? backLabel(changesAt) : "Until they turn it back on";
    }
    if (entry.presence?.availabilityMode === "WORKING_HOURS") {
      return workingHoursSummary(entry.presence.workingHours);
    }
    return "";
  };

  const hoursPresence = hoursMember ? availabilityById.get(String(hoursMember.id))?.presence : null;

  const saveMemberHours = async (request: WorkingHoursRequest) => {
    if (!hoursMember) {
      return;
    }
    await setMemberWorkingHours({ membershipId: hoursMember.id, data: request }).unwrap();
  };

  if (meLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Members (non-admins) can't view or manage the team — show a clear access notice.
  if (!canManage) {
    return (
      <div className="flex items-center justify-center h-64 p-4">
        <Card className="max-w-md text-center">
          <CardHeader>
            <CardTitle>Restricted</CardTitle>
            <CardDescription>
              Only organization owners and admins can manage the team. Contact an admin if you need access.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading team members...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-destructive">Failed to load team members</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Team</h1>
          <p className="text-muted-foreground">Manage your organization's people, permissions and teams</p>
        </div>
        {activeTab === "teams" ? (
          <Button onClick={() => setIsNewTeamOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New team
          </Button>
        ) : (
          <Button onClick={() => setInviteDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Invite Member
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="members">Team members</TabsTrigger>
          <TabsTrigger value="teams">Teams</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-6">
          {statusFilter === "all" && memberList.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Users className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No team members yet</h3>
                <p className="text-muted-foreground text-center mb-4 max-w-md">
                  Invite team members to collaborate and manage calls together.
                </p>
                <Button onClick={() => setInviteDialogOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Invite Your First Member
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Team Members</CardTitle>
                    <CardDescription>
                      {memberList.length} {memberList.length === 1 ? "member" : "members"}
                    </CardDescription>
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Members</SelectItem>
                      <SelectItem value={MembershipStatus.ACTIVE}>Active</SelectItem>
                      <SelectItem value={MembershipStatus.PENDING}>Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Availability</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {memberList.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground">
                          {statusFilter === MembershipStatus.PENDING ? "No pending invitations" : "No active members"}
                        </TableCell>
                      </TableRow>
                    )}
                    {memberList.map((member) => {
                      const RoleIcon = roleIcons[member.role];
                      const availabilityEntry = availabilityById.get(String(member.id));
                      return (
                        <TableRow key={member.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar>
                                <AvatarImage src={member.user.profilePicture} />
                                <AvatarFallback>{getInitials(member.user.firstName, member.user.lastName)}</AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">{memberName(member)}</div>
                                <div className="text-sm text-muted-foreground">
                                  {member.displayName
                                    ? `${member.user.firstName} ${member.user.lastName} · ${member.user.email}`
                                    : member.user.email}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className={`p-1 rounded ${roleColors[member.role]} text-white`}>
                                <RoleIcon className="h-3 w-3" />
                              </div>
                              <span>{roleLabels[member.role]}</span>
                              {member.role !== MembershipRole.OWNER && member.exportAccess === "ALLOWED" && (
                                <span className="status-pill-info">Can export</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {availabilityEntry && (
                              <div className="space-y-0.5">
                                <MemberAvailabilityStatus availability={availabilityEntry.availability} />
                                <div className="text-xs text-muted-foreground">{availabilityDetail(availabilityEntry)}</div>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={statusColors[member.status]}>
                              {member.status === MembershipStatus.PENDING && <Clock className="mr-1 h-3 w-3" />}
                              {member.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {member.status === MembershipStatus.PENDING ? (
                              <div className="text-sm text-muted-foreground">
                                Invited {new Date(member.invitationSentAt!).toLocaleDateString()}
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground">
                                {new Date(member.createdAt).toLocaleDateString()}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {member.role !== MembershipRole.OWNER &&
                              String(member.id) !== String(me?.activeMembership?.id) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {member.status === MembershipStatus.PENDING && (
                                    <>
                                      <DropdownMenuItem onClick={() => handleResendInvitation(member.id)}>
                                        <Mail className="mr-2 h-4 w-4" />
                                        Resend Invitation
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                    </>
                                  )}

                                  {member.status === MembershipStatus.ACTIVE && (
                                    <>
                                      {isOwner && member.role !== MembershipRole.ADMIN && (
                                        <DropdownMenuItem
                                          onClick={() => handleUpdateRole(member.id, MembershipRole.ADMIN)}
                                        >
                                          <ShieldCheck className="mr-2 h-4 w-4" />
                                          Promote to Admin
                                        </DropdownMenuItem>
                                      )}

                                      {member.role === MembershipRole.ADMIN && (
                                        <DropdownMenuItem
                                          onClick={() => handleUpdateRole(member.id, MembershipRole.MEMBER)}
                                        >
                                          <Shield className="mr-2 h-4 w-4" />
                                          Demote to Member
                                        </DropdownMenuItem>
                                      )}

                                      {member.role !== MembershipRole.AI_SUPERVISOR && (
                                        <DropdownMenuItem
                                          onClick={() => handleUpdateRole(member.id, MembershipRole.AI_SUPERVISOR)}
                                        >
                                          <Bot className="mr-2 h-4 w-4" />
                                          Make AI supervisor
                                        </DropdownMenuItem>
                                      )}

                                      {member.role === MembershipRole.AI_SUPERVISOR && (
                                        <DropdownMenuItem
                                          onClick={() => handleUpdateRole(member.id, MembershipRole.MEMBER)}
                                        >
                                          <Shield className="mr-2 h-4 w-4" />
                                          Make Member
                                        </DropdownMenuItem>
                                      )}

                                      {isOwner && (
                                        <DropdownMenuItem
                                          onClick={() => handleExportAccess(member, member.exportAccess !== "ALLOWED")}
                                        >
                                          <FileDown className="mr-2 h-4 w-4" />
                                          {member.exportAccess === "ALLOWED" ? "Remove export permission" : "Allow exports"}
                                        </DropdownMenuItem>
                                      )}

                                      <DropdownMenuItem onClick={() => setNameMember(member)}>
                                        <Pencil className="mr-2 h-4 w-4" />
                                        Edit display name
                                      </DropdownMenuItem>

                                      <DropdownMenuItem onClick={() => setHoursMember(member)}>
                                        <CalendarDays className="mr-2 h-4 w-4" />
                                        Working hours
                                      </DropdownMenuItem>

                                      <DropdownMenuSeparator />
                                    </>
                                  )}

                                  <DropdownMenuItem onClick={() => setMemberToRemove(member)} className="text-destructive">
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Remove Member
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="teams" className="mt-6">
          <TeamsTab onCreateTeam={() => setIsNewTeamOpen(true)} />
        </TabsContent>
      </Tabs>

      <TeamDialog open={isNewTeamOpen} onOpenChange={setIsNewTeamOpen} team={null} />

      <InviteMemberDialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen} canInviteAdmins={isOwner} />

      <DisplayNameDialog
        member={nameMember}
        onOpenChange={(open) => {
          if (!open) {
            setNameMember(null);
          }
        }}
      />

      <WorkingHoursDialog
        open={!!hoursMember}
        onOpenChange={(open) => {
          if (!open) {
            setHoursMember(null);
          }
        }}
        title={`${memberFirstName(hoursMember)}'s working hours`}
        presence={hoursPresence}
        defaultTimeZone={me?.activeOrganisation?.timezone ?? "America/New_York"}
        followHours={hasWorkingHours(hoursPresence?.workingHours) ? undefined : true}
        onSave={saveMemberHours}
      />

      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              <span className="font-semibold">
                {memberToRemove?.user.firstName} {memberToRemove?.user.lastName}
              </span>{" "}
              from your organization? They will lose access to all resources.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="bg-destructive">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
